// src/pages/StartedGroups.js
import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import {
  GroupsOutlined,
  CalendarMonth,
  PeopleAlt,
  AttachMoney,
  PlayArrow,
  CheckCircle,
  Close,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData, postData } from "../services/request";

// Helper Functions
const toGroups = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.groups)) return response.groups;
  return response?.id ? [response] : [];
};

const isGroupActive = (group) => group?.isActive !== false;
const groupKey = (group, index) => group?.id || `${group?.title}-${index}`;

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (value, symbol = "NGN") => {
  const numericValue = Number(value || 0);
  return `${symbol} ${numericValue.toLocaleString("en-US")}`;
};

const getStoredUser = () =>
  JSON.parse(localStorage.getItem("userData") || "{}");
const getUserRole = (user) => user?.userType || "groupUser";

const StartedGroups = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Modal & API States
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const user = getStoredUser();
  const role = getUserRole(user);
  const displayName = user?.fullName || user?.full_name || "Dajoo member";
  const symbol = user?.country?.symbol || user?.country?.currency || "NGN";

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  useEffect(() => {
    getData("/api/group/fetch_created")
      .then((response) => setGroups(toGroups(response)))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  // ✅ Get GroupPlan Status
  const getGroupPlanStatus = (group) => {
    return group?.GroupPlan?.status || "InActive"; // Default InActive
  };

  // ✅ Start Collection Button Click (Fee Calculation API)
  const handleStartCollection = async (group) => {
    setFeeLoading(true);
    setActionMessage("");
    try {
      const payload = {
        groupId: group.id,
      };

      const response = await postData("/api/group/group-fee", payload);

      if (response?.success === true) {
        setFeeData(response.data);
        setFeeModalOpen(true);
      } else {
        alert(response?.message || "Failed to calculate fees");
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Please try again.");
    } finally {
      setFeeLoading(false);
    }
  };

  // ✅ Final Start Collection (Modal ke andar wala button)
  const handleFinalStartCollection = async () => {
    if (!feeData?.groupId) return;

    setActionLoading(true);
    setActionMessage("");
    try {
      const payload = {
        groupId: feeData.groupId,
      };

      const response = await postData("/api/group/start_collection", payload);

      if (response?.success === true) {
        setActionMessage(
          response.message || "Collection started successfully!",
        );
        setTimeout(() => {
          setFeeModalOpen(false);
          setFeeData(null);
          // Refresh groups after success
          getData("/api/group/fetch_created")
            .then((res) => setGroups(toGroups(res)))
            .catch(() => {});
        }, 2000);
      } else {
        setActionMessage(response?.message || "Failed to start collection");
      }
    } catch (error) {
      console.error(error);
      setActionMessage("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Start Contribution Click - Group Status Check
  const handleStartContribution = async (group) => {
    setStatusLoading(true);
    try {
      const payload = {
        groupId: group.id,
      };

      const response = await postData("/api/group/group-status", payload);

      // Store response in state
      setStatusData({
        response: response,
        groupId: group.id,
        groupTitle: group.title,
      });
      setStatusModalOpen(true);
    } catch (error) {
      console.error(error);
      alert("Network error. Please try again.");
    } finally {
      setStatusLoading(false);
    }
  };

  // ✅ Redirect to Contribution Page from Modal
  const handleGoToContributionPage = () => {
    const groupId = statusData?.groupId;
    if (groupId) {
      navigate(`/started-contribution?groupId=${groupId}`);
    }
    setStatusModalOpen(false);
  };

  const handleCloseModal = () => {
    setFeeModalOpen(false);
    setFeeData(null);
    setActionMessage("");
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setStatusData(null);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f7fb", color: "#1d2433" }}>
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: role }}
      />
      <Sidebar
        role={role}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 1440, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              Started Groups
            </Typography>
            <Typography sx={{ color: "#7a8394", mt: 0.7 }}>
              Review and manage every group you created.
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ minHeight: 190, display: "grid", placeItems: "center" }}>
              <CircularProgress sx={{ color: "#52057b" }} />
            </Box>
          ) : groups.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                border: "1px solid #e9ebf0",
                borderRadius: 3,
                boxShadow: "none",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                You have not created any groups yet.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {groups.map((group, index) => {
                const active = isGroupActive(group);
                const totalMembers = group?.totalUsers || 1;
                const planStatus = getGroupPlanStatus(group);

                return (
                  <Grid item xs={12} sm={6} lg={4} key={groupKey(group, index)}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #e9ebf0",
                        borderRadius: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {/* Group Image Header */}
                      <Box
                        sx={{
                          height: 120,
                          bgcolor: "#f7f1fa",
                          display: "grid",
                          placeItems: "center",
                          position: "relative",
                        }}
                      >
                        {group.groupImage ? (
                          <Box
                            component="img"
                            src={group.groupImage}
                            alt={group.title}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <GroupsOutlined
                            sx={{ fontSize: 48, color: "#52057b" }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={active ? "Active" : "Inactive"}
                          color={active ? "success" : "default"}
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            fontWeight: 700,
                          }}
                        />
                      </Box>

                      {/* Card Content */}
                      <Box sx={{ p: 2.5, flex: 1 }}>
                        <Typography
                          sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}
                        >
                          {group.title || "Untitled group"}
                        </Typography>
                        <Typography
                          sx={{ color: "#7a8394", fontSize: 13, mb: 1.5 }}
                        >
                          {group.description || "No description available"}
                        </Typography>

                        {/* Meta Info */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={formatCurrency(group.amount, symbol)}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            icon={<PeopleAlt />}
                            label={`${totalMembers} members`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            icon={<CalendarMonth />}
                            label={formatDate(group.createdAt)}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                        </Box>

                        {/* Plan Status Chip */}
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={`Plan Status: ${planStatus}`}
                            size="small"
                            sx={{
                              bgcolor:
                                planStatus === "completed"
                                  ? "#e8f5e9"
                                  : planStatus === "pending_payment"
                                    ? "#fff3e0"
                                    : planStatus === "Active"
                                      ? "#e8f5e9"
                                      : "#f7f1fa",
                              color:
                                planStatus === "completed"
                                  ? "#2e7d32"
                                  : planStatus === "pending_payment"
                                    ? "#e65100"
                                    : planStatus === "Active"
                                      ? "#2e7d32"
                                      : "#52057b",
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Action Footer - Active Status par Button Hide */}
                      <Box
                        sx={{
                          p: 2,
                          borderTop: "1px solid #f0f1f4",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {planStatus === "Active" ? (
                          // ✅ Active Status - Koi Button Nahi
                          <Chip
                            label="Active"
                            sx={{
                              bgcolor: "#e8f5e9",
                              color: "#2e7d32",
                              fontWeight: 700,
                              width: "100%",
                              py: 1.5,
                            }}
                          />
                        ) : planStatus === "InActive" ? (
                          <Button
                            variant="contained"
                            startIcon={
                              feeLoading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <PlayArrow />
                              )
                            }
                            disabled={feeLoading}
                            onClick={() => handleStartCollection(group)}
                            sx={{
                              bgcolor: "#52057b",
                              color: "#fff",
                              borderRadius: "12px",
                              width: "100%",
                              py: 1.2,
                              textTransform: "none",
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "#3e055e",
                              },
                            }}
                          >
                            Start Collection
                          </Button>
                        ) : planStatus === "pending_payment" ||
                          planStatus === "completed" ? (
                          <Button
                            variant="contained"
                            startIcon={
                              statusLoading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CheckCircle />
                              )
                            }
                            disabled={statusLoading}
                            onClick={() => handleStartContribution(group)}
                            sx={{
                              bgcolor: "#2e7d32",
                              color: "#fff",
                              borderRadius: "12px",
                              width: "100%",
                              py: 1.2,
                              textTransform: "none",
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "#1b5e20",
                              },
                            }}
                          >
                            Start Contribution
                          </Button>
                        ) : (
                          <Chip
                            label="Unknown Status"
                            sx={{
                              bgcolor: "#f0f0f0",
                              color: "#777",
                              fontWeight: 700,
                              width: "100%",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      {/* ✅ Fee Calculation Modal */}
      <Dialog
        open={feeModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f7f1fa",
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "#52057b" }}>
            Fee Breakdown
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {feeData && (
            <Box>
              {/* Group Info */}
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2 }}>
                {feeData.groupTitle}
              </Typography>

              {/* Plan Details */}
              <Box
                sx={{
                  backgroundColor: "#f7f1fa",
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Plan Details
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Monthly Amount:{" "}
                  <b>
                    {formatCurrency(feeData.planDetails.monthlyAmount, symbol)}
                  </b>
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Duration: <b>{feeData.planDetails.durationMonths} months</b>
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Base Total:{" "}
                  <b>
                    {formatCurrency(
                      feeData.planDetails.baseTotalAmount,
                      symbol,
                    )}
                  </b>
                </Typography>
              </Box>

              {/* Fee Breakdown */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Fee Breakdown
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Admin Fee:{" "}
                  <b>
                    {formatCurrency(
                      feeData.feeBreakdown.adminFee.amount,
                      symbol,
                    )}
                  </b>{" "}
                  ({feeData.feeBreakdown.adminFee.rate})
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Insurance Fee:{" "}
                  <b>
                    {formatCurrency(
                      feeData.feeBreakdown.insuranceFee.amount,
                      symbol,
                    )}
                  </b>{" "}
                  ({feeData.feeBreakdown.insuranceFee.rate})
                </Typography>
                <Typography sx={{ fontSize: 13, mt: 1 }}>
                  Total Charge:{" "}
                  <b style={{ color: "#52057b" }}>
                    {formatCurrency(feeData.feeBreakdown.totalCharge, symbol)}
                  </b>
                </Typography>
              </Box>

              {/* Per User Breakdown */}
              {feeData.perUserBreakdown && (
                <Box
                  sx={{
                    backgroundColor: "#f7f1fa",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                    Per User Breakdown
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Users: <b>{feeData.perUserBreakdown.usersCount}</b>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Per User Total:{" "}
                    <b>
                      {formatCurrency(
                        feeData.perUserBreakdown.perUserTotal,
                        symbol,
                      )}
                    </b>
                  </Typography>
                </Box>
              )}

              {/* Validation Warning */}
              {feeData.validation && (
                <Alert
                  severity={feeData.validation.isValid ? "success" : "warning"}
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  {feeData.validation.message}
                </Alert>
              )}

              {/* Requirements Warning */}
              {feeData.requirements && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  {feeData.requirements.monthlyAmountMessage}
                </Alert>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: "#52057b",
                    color: "#52057b",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleFinalStartCollection}
                  disabled={actionLoading || !feeData.validation?.isValid}
                  startIcon={
                    actionLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayArrow />
                    )
                  }
                  sx={{
                    bgcolor: "#52057b",
                    color: "#fff",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      bgcolor: "#3e055e",
                    },
                  }}
                >
                  Start Collection
                </Button>
              </Box>

              {/* Success/Error Message */}
              {actionMessage && (
                <Alert
                  severity="success"
                  sx={{ mt: 2, borderRadius: 2 }}
                  onClose={() => setActionMessage("")}
                >
                  {actionMessage}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ✅ Group Status Modal */}
      <Dialog
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f7f1fa",
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "#52057b" }}>
            Group Status
          </Typography>
          <IconButton onClick={handleCloseStatusModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {statusData && (
            <Box>
              {/* Group Title */}
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2 }}>
                {statusData.groupTitle}
              </Typography>

              {/* Success / Error based on response */}
              {statusData.response?.success === true ? (
                <>
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    {statusData.response?.message || "All payments completed!"}
                  </Alert>

                  {/* Payment Details */}
                  <Box
                    sx={{
                      backgroundColor: "#e8f5e9",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                      Payment Details
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      Total Charge:{" "}
                      <b>
                        {formatCurrency(
                          statusData.response?.data?.totalCharge,
                          symbol,
                        )}
                      </b>
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      Admin Fee:{" "}
                      <b>
                        {formatCurrency(
                          statusData.response?.data?.adminFee,
                          symbol,
                        )}
                      </b>
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      Insurance Fee:{" "}
                      <b>
                        {formatCurrency(
                          statusData.response?.data?.insuranceFee,
                          symbol,
                        )}
                      </b>
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      Users Paid: <b>{statusData.response?.data?.usersPaid}</b>{" "}
                      / {statusData.response?.data?.usersCount}
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      Status: <b>{statusData.response?.data?.status}</b>
                    </Typography>
                  </Box>

                  {/* Go to Contribution Page Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGoToContributionPage}
                    startIcon={<CheckCircle />}
                    sx={{
                      bgcolor: "#52057b",
                      color: "#fff",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: "#3e055e",
                      },
                    }}
                  >
                    Go to Contribution Page
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {statusData.response?.message || "Failed to check status"}
                  </Alert>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCloseStatusModal}
                    sx={{
                      borderColor: "#52057b",
                      color: "#52057b",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Close
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StartedGroups;
